import React, { useCallback, useMemo, useState } from 'react';

import { BugIcon, RocketIcon } from '@primer/octicons-react';
import { StyledOcticon } from '@primer/react';

import BasicDropdown from '../../../common/BasicDropdown/BasicDropdown';
import Button from '../../../common/Button/Button';
import CardView from '../../../common/CardView/CardView';
import ErrorSlate from '../../../ErrorSlate/ErrorSlate';
import LoadingSpinner from '../../../common/LoadingSpinner/LoadingSpinner';
import PaginationRow from '../../../common/PaginationRow/PaginationRow';
import SearchInput from '../../../common/TextInputs/SearchInput/SearchInput';

import FilterCountButton from '../../FilterCountButton';
import OrchestrationAdminTable from '../OrchestrationAdminTable/OrchestrationAdminTable';

import OrchestrationTableFilterModal, {
  RELATIVE_TIME_RANGES,
  ORCHESTRATION_STATUS as STATUS,
  TASK_RUN_STATUS, ALL_TIME,
} from '../OrchestrationTableFilterModal/OrchestrationTableFilterModal';

import AdminBulkOrchestrationRemovalModal from '../AdminBulkOrchestrationRemovalModal/AdminBulkOrchestrationRemovalModal';
import AdminOrchestrationRemovalModal from '../AdminOrchestrationRemovalModal/AdminOrchestrationRemovalModal';

import deleteOrchestrations from '../../../../utils/api_functions/deleteOrchestration';
import getDownloadOrchestrationURL from '../../../../utils/api_functions/downloadOrchestration';
import useAllOrchestrations from '../useAllOrchestrations';
import useWorkspaceContext from '../../../workspaces/WorkspaceContext/WorkspaceContext';
import classNames from 'classnames';

const ACTION_REMOVE_ORCHESTRATIONS = 'removeOrchestrations';
const BULK_ACTIONS = [
  {
    id: ACTION_REMOVE_ORCHESTRATIONS,
    label: 'Delete',
  }
]

const ORCHESTRATION_ADMIN_MODAL = {
  DELETE_ORCHESTRATION: 'delete_orchestration',
  BULK_DELETE: 'bulk_delete',
  FILTER_ORCHESTRATIONS: 'filter_orchestrations',
}

const DEFAULT_FILTER = {
  workspaces: [],
  timeRange: RELATIVE_TIME_RANGES[0],
  status: STATUS.All,
  taskRunStatus: TASK_RUN_STATUS.All,
};

const BugGraphic = () => (
  <StyledOcticon
    className="mb-2 text-brand"
    icon={BugIcon}
    size={64}
  />
)

const NoOrchestrationsGraphic = () => (
  <StyledOcticon
    className="mb-2 text-brand"
    icon={RocketIcon}
    size={64}
  />
)

const countActiveOrchestrationFilters = (filterObj = {}) => {
  let filterCount = 0;
  if (filterObj.status !== STATUS.All) {
    filterCount += 1;
  }
  if (filterObj.taskRunStatus !== TASK_RUN_STATUS.All) {
    filterCount += 1;
  }
  if (filterObj.workspaces?.length) {
    filterCount += 1;
  }
  if (filterObj.timeRange?.fromDate) {
    filterCount += 1;
  }

  return filterCount;
}

export const OrchestrationAdminTab = ({
  error = null,
  loading = false,
  workspaces = [],
  orchestrations = [],
  totalOrchestrationCount = 0,
  searchQuery = '',
  filterObj = DEFAULT_FILTER,
  setSearchQuery = () => {},
  setFilterObj = () => {},
}) => {
  const [isSelectAllChecked, setSelectAllChecked] = useState(false);
  const [selectedOrchestrations, setSelectedOrchestrations] = useState([]);
  const [selectedPage, setSelectedPage] = useState(1);
  const [resultsPerPage, setResultsPerPage] = useState(25);

  const [selectedTableAction, setSelectedTableAction] = useState(null);

  const [showModal, setShowModal] = useState(null);
  // Either ID or Orchestration object as needed
  const [focusedOrchestration, setFocusedOrchestration] = useState(null)
  const [modalError, setModalError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const { setCurrentWorkspaceId } = useWorkspaceContext();

  const handleBulkActionApply = useCallback(() => {
    if (selectedTableAction === ACTION_REMOVE_ORCHESTRATIONS) {
      setShowModal(ORCHESTRATION_ADMIN_MODAL.BULK_DELETE);
    }
  }, [selectedTableAction])

  const handleBulkActionCancel = () => {
    setSelectedTableAction(null);
    setSelectedOrchestrations([])
    setSelectAllChecked(false);
  }

  const handleSearchInputChange = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, [setSearchQuery])

  const handleOrchestrationRemoval = (orchId) => {
    setModalError(null);
    setActionLoading(true);
    deleteOrchestrations([orchId]).then((res) => {
      setActionLoading(false);
      if (res?.code && res.code >= 400) {
        const message = res?.msg || 'An unexpected error has occurred while trying to remove the selected orchestration. Please try again in a few minutes.';
        setModalError(message);
      } else {
        setShowModal(null);
        setFocusedOrchestration(null);
        setModalError(null);
        setSelectedTableAction(null);
        setSelectedOrchestrations([]);
        setSelectAllChecked(false);
      }
    }).catch((e) => {
      setActionLoading(false);
      if (e?.msg) {
        setModalError(e.msg);
      } else {
        setModalError('An unexpected error occurred while trying to remove the selected orchestration. Please try again in a few minutes.')
      }
    })
  }

  const handleBulkOrchestrationRemoval = (orchIds) => {
    setModalError(null);
    setActionLoading(true);
    deleteOrchestrations(orchIds).then((res) => {
      setActionLoading(false);
      if (res?.code && res.code >= 400) {
        const message = res?.msg || 'An unexpected error has occurred while trying to remove the selected orchestration(s). Please try again in a few minutes.';
        setModalError(message);
      } else {
        setShowModal(null);
        setFocusedOrchestration(null);
        setModalError(null);
        setSelectedTableAction(null);
        setSelectedOrchestrations([]);
        setSelectAllChecked(false);
      }
    }).catch((e) => {
      setActionLoading(true);
      if (e?.msg) {
        setModalError(e.msg);
      } else {
        setModalError('An unexpected error occurred while trying to remove the selected orchestration(s). Please try again in a few minutes.')
      }
    })
  }

  const viewTaskRuns = (original) => {
    const hasTimeRange = filterObj.timeRange && filterObj.timeRange.type !== ALL_TIME;
    const hasCustomRange = filterObj.timeRange.type === 'custom';

    setCurrentWorkspaceId(original?.workspace_id);
    let route = `workspaces/${original?.workspace_id}/orchestrations/${original?.id}/taskRuns/status/${filterObj.taskRunStatus}`
    if (hasTimeRange) {
      if (hasCustomRange) {
        const fromDate = filterObj.timeRange?.fromDate;
        const toDate = filterObj.timeRange?.toDate;

        const fromMillis = fromDate.getTime();
        const toMillis = toDate.getTime();

        route =`/workspaces/${original?.workspace_id}/orchestrations/${original?.id}/taskRuns/status/${filterObj.taskRunStatus}/range/${fromMillis}/${toMillis}`;
      } else {
        const durationId = filterObj.timeRange.id;
        route = `/workspaces/${original?.workspace_id}/orchestrations/${original?.id}/taskRuns/status/${filterObj.taskRunStatus}/duration/${durationId}`;
      }
    }

    window.open(`${window.location.origin}/${route}`, '_blank')
  }

  const onShowOrchestrationRemovalModal = (orch) => {
    setShowModal(ORCHESTRATION_ADMIN_MODAL.DELETE_ORCHESTRATION)
    setFocusedOrchestration(orch);
    setModalError(null);
  }

  const onModalToggle = (orch) => {
    if (!orch) {
      setShowModal(null);
      setFocusedOrchestration(null);
      setModalError(null);
    }
  }

  const onModalClose = () => {
    setShowModal(null);
    setFocusedOrchestration(null);
    setModalError(null);
  }

  const onBulkUserRemovalDialogToggle = (orchestrationsToRemove) => {
    if (!orchestrationsToRemove) {
      setShowModal(null);
      setFocusedOrchestration(null);
      setModalError(null);
    }
  }

  const onFilterConfirmed = (filter) => {
    setFilterObj(filter);
    setShowModal(null);
    setModalError(null);
  }

  const onFilterClear = () => {
    setFilterObj(DEFAULT_FILTER)
    setShowModal(null);
    setModalError(null);
  }

  const handleOrchestrationClick = useCallback(({ original }) => {
    setCurrentWorkspaceId(original?.workspace_id);

    const route = `workspaces/${original?.workspace_id}/orchestrations/${original?.id}`;
    window.open(`${window.location.origin}/${route}`, '_blank')
  }, [setCurrentWorkspaceId])

  const handleOrchestrationDownload = useCallback(({ original }) => {
    setCurrentWorkspaceId(original?.workspace_id);

    const route = getDownloadOrchestrationURL(original?.id);
    window.open(route, '_blank')
  }, [setCurrentWorkspaceId])

  const formatResultsCount = useCallback(val => `${val}/${totalOrchestrationCount} results`, [totalOrchestrationCount]);

  const errorText = error ? 'An unexpected error occurred while trying to load the workload list.' : 'No workloads found.';

  // Hides the CTA section if there are simply no users to display
  const errorProps = error ? {} : { CTAInfo: null }

  return (
    <div>
      <div className="flex items-center justify-start mb-2">
        <div className="flex items-center justify-start mx-2">
          <BasicDropdown
            direction="bottom-left"
            dropdownLabel={selectedTableAction ? '' : 'Bulk Actions'}
            values={BULK_ACTIONS}
            selectedValue={selectedTableAction}
            onValueSelected={setSelectedTableAction}
          />
          {
            selectedTableAction && (
              <>
                <Button className="mx-1" size="large" variant="danger" onClick={handleBulkActionApply} disabled={!selectedOrchestrations?.length}>
                  {
                    selectedOrchestrations?.length ? `Apply (${selectedOrchestrations.length})` : 'Apply'
                  }
                </Button>
                <Button size="large" onClick={handleBulkActionCancel}>
                  Cancel
                </Button>
              </>
            )
          }
        </div>
        <SearchInput className="mx-2" value={searchQuery} onChange={handleSearchInputChange} block />
        <FilterCountButton activeFilters={countActiveOrchestrationFilters(filterObj)} onClick={() => setShowModal(ORCHESTRATION_ADMIN_MODAL.FILTER_ORCHESTRATIONS)}>Filter</FilterCountButton>
      </div>
      <div className="relative">
        {
          (loading || actionLoading) && (
            <LoadingSpinner
              animationKey="orchestration-admin-table-loading-spinner"
              loadingText="Loading..."
              useOverlay
            />
          )
        }
        <div className={classNames('border shadow', {
          'hidden': !orchestrations?.length || error,
        })}>
          <OrchestrationAdminTable
            orchestrations={orchestrations}
            selectedOrchestrations={selectedOrchestrations}
            setSelectedOrchestrations={setSelectedOrchestrations}
            viewTaskRuns={viewTaskRuns}
            downloadOrchestration={handleOrchestrationDownload}
            deleteOrchestration={onShowOrchestrationRemovalModal}
            onOrchestrationClick={handleOrchestrationClick}
            hideCheckboxes={!selectedTableAction}
            isSelectionColumnChecked={isSelectAllChecked}
            setSelectionColumnChecked={setSelectAllChecked}
            selectedPage={selectedPage}
            resultsPerPage={resultsPerPage}
          />
        </div>
        {
          (!orchestrations?.length || error) && (
            <CardView className="p-4" disableFullHeight>
              <ErrorSlate
                errorText={errorText}
                subText={null}
                Graphic={error ? BugGraphic : NoOrchestrationsGraphic}
                {...errorProps}
              />
            </CardView>
          )
        }
      </div>
      <PaginationRow
        className="p-4"
        numberOfResults={orchestrations?.length}
        selectedPage={selectedPage}
        resultsPerPage={resultsPerPage}
        setSelectedPage={setSelectedPage}
        setResultsPerPage={setResultsPerPage}
        formatResultsCount={formatResultsCount}
      />
      {
        showModal === ORCHESTRATION_ADMIN_MODAL.FILTER_ORCHESTRATIONS && (
          <OrchestrationTableFilterModal
            filter={filterObj}
            workspaces={workspaces}
            showDialog={showModal === ORCHESTRATION_ADMIN_MODAL.FILTER_ORCHESTRATIONS}
            onDialogToggle={onModalToggle}
            onFilterConfirm={onFilterConfirmed}
            onClear={onFilterClear}
          />
        )
      }
      {
        showModal === ORCHESTRATION_ADMIN_MODAL.DELETE_ORCHESTRATION && (
          <AdminOrchestrationRemovalModal
            error={modalError}
            orchestration={focusedOrchestration}
            showDialog={showModal === ORCHESTRATION_ADMIN_MODAL.DELETE_ORCHESTRATION}
            onOrchestrationRemoved={handleOrchestrationRemoval}
            onDialogToggle={onModalToggle}
          />
        )
      }
      {
        showModal === ORCHESTRATION_ADMIN_MODAL.BULK_DELETE && (
          <AdminBulkOrchestrationRemovalModal
            error={modalError}
            orchestrations={selectedOrchestrations}
            showDialog={showModal === ORCHESTRATION_ADMIN_MODAL.BULK_DELETE}
            onOrchestrationsRemoved={handleBulkOrchestrationRemoval}
            onDialogToggle={onBulkUserRemovalDialogToggle}
          />
        )
      }
    </div>
  )
}

const handleSearch = (orchestrations, searchQuery, filterObj) => orchestrations.filter((orchestrationObj) => {
  const searchTerm = searchQuery?.toLowerCase() || '';

  const {
    orchestration, workspace,
  } = orchestrationObj;

  if (filterObj?.status !== STATUS.All) {
    if (orchestration?.status === STATUS.Active && filterObj.status !== STATUS.Active) {
      return false;
    }
    if (orchestration?.status !== STATUS.Active && filterObj.status === STATUS.Active) {
      return false;
    }
  }

  // Name can be null for orchestrations
  const nameCheck = !orchestration?.name || orchestration?.name.toLowerCase().includes(searchTerm);

  // Orchestration name, workspace name, workspace owner email, created by user email, file hash
  return nameCheck
    || orchestration?.created_by?.toLowerCase()?.includes(searchTerm)
    || workspace?.created_by?.toLowerCase()?.includes(searchTerm)
    || orchestration?.sha?.toLowerCase().includes(searchTerm)
    || workspace?.name?.toLowerCase().includes(searchTerm);
})

const ContextualOrchestrationAdminTab = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterObj, setFilterObj] = useState(DEFAULT_FILTER)

  const {
    data: orchestrations,
    loading,
    error
  } = useAllOrchestrations(filterObj);
  
  const { workspaces = [] } = useWorkspaceContext();

  const filteredOrchestrations = useMemo(() => handleSearch(orchestrations, searchQuery, filterObj), [filterObj, searchQuery, orchestrations]);

  return (
    <OrchestrationAdminTab
      error={error}
      loading={loading}
      workspaces={workspaces}
      orchestrations={filteredOrchestrations}
      totalOrchestrationCount={orchestrations?.length || 0}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      filterObj={filterObj}
      setFilterObj={setFilterObj}
    />
  )
}

export default ContextualOrchestrationAdminTab;
